/**
 * DecisionFlow — CompensationPoliciesWorkspace
 * ui/src/components/admin/CompensationPoliciesWorkspace.jsx
 * S547 — §6 Six-Stage Bulk Assignment Wizard mounted in AssignmentsTab
 *
 * Workspace shell at /admin/compensation.
 * Five peer tabs — not nested, not wizard-only.
 *
 *   Plans | Templates | Components | Assignments | Approvals
 *
 * Tab persistence: sessionStorage key "comp-workspace-tab".
 * Deep linking: ?tab=plans, ?tab=templates, etc. via useSearchParams.
 * Global search header: searches across all tabs simultaneously.
 *
 * S547 changes (AssignmentsTab only — all other tabs unchanged):
 *   "New Bulk Assignment" button added to AssignmentsTab header.
 *   BulkAssignmentWizard modal mounted when button clicked.
 *   fiscalPeriod prop passed to wizard — period locked from tab context.
 *   PlanAssignmentManager unchanged — exception path below wizard.
 *
 * S545 changes (preserved):
 *   AssignmentsTab renders Population Analysis + Conflict Detection
 *   panels above PlanAssignmentManager.
 *
 *   1. Population Analysis panel
 *      GET /api/v1/plan-assignments/analyze?fiscal_period_id={id}
 *      Fires on tab mount when period is available.
 *      Displays per-component summary with health badges per §6.
 *      Static placeholder when no fiscal period or empty response.
 *      No mock data.
 *
 *   2. Conflict Detection panel
 *      GET /api/v1/plan-assignments/conflicts?fiscal_period_id={id}
 *      Fires on-demand via "Check for Conflicts" button per §6.
 *      Conflicts grouped by category, error (red) vs warning (amber).
 *      Resolution text shown per conflict.
 *      Total counts (errors + warnings) in header.
 *      Static placeholder on empty. No mock data.
 *
 * Confirmed endpoints (all live at 1137cbe):
 *   GET /api/v1/plans
 *   GET /api/v1/template-library/templates
 *   GET /api/v1/template-library/modules
 *   GET /api/v1/plan-assignments
 *   GET /api/v1/plan-assignments/analyze          ← S545 NEW
 *   GET /api/v1/plan-assignments/conflicts        ← S545 NEW
 *   GET /api/v1/plans?plan_status=pending_approval
 *   GET /api/v1/plan-components
 *   GET /api/v1/fiscal-periods
 *   POST /api/v1/plan-components/{id}/approve
 *   POST /api/v1/amendments/{id}/commit
 *
 * Controls (non-negotiable):
 *   - No P4-xx codes on any user-facing surface
 *   - NN-36: two-actor approval — self-approval blocked
 *   - Static placeholder for empty API responses — no mock data
 *   - No AI in governance path (NN-03)
 *   - Drag-drop = display order only (stated explicitly where applicable)
 *   - Frontend never calculates entitlement
 *   - Error envelope: resp.json()["detail"]["error"]["code"]
 *
 * Spec authority: comp_policies_spec_s539.md §6 (Assignments), §2, §3, §7A
 * CSS: index.css tokens — rgb(var(--color-*)), skeleton shimmer, IBM Plex Sans
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.jsx";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, FileCheck2, Layers3, Workflow, CheckCircle2,
  Sparkles, AlertTriangle, XCircle, X, Info,
  Upload, Download, CheckCircle, AlertCircle,
  RefreshCw, ShieldAlert, ShieldCheck, Clock,
  History, ChevronUp, ChevronDown, User, Lock,
} from "lucide-react";

// S548 — §6 Assignment History ledger drawer
import PositionAssignmentLedger from "./PositionAssignmentLedger.jsx";

// S548 — §5 Component Block Builder (visual mode)
import ComponentBlockBuilder from "./ComponentBlockBuilder.jsx";

import Button            from "../shared/Button.jsx";
import Card              from "../shared/Card.jsx";
import Field             from "../shared/Field.jsx";
import Pill              from "../shared/Pill.jsx";
import AmendmentBeginModal   from "./AmendmentBeginModal.jsx";
import BulkAssignmentWizard               from "./BulkAssignmentWizard.jsx";
import PlanLibrary                        from "./PlanLibrary.jsx";
import CoverageView                       from "./CoverageView.jsx";
import PositionPlanAssignmentWorkspace    from "./PositionPlanAssignmentWorkspace.jsx";
// TemplateLibrary.jsx preserved as reference — not mounted (spec §14)
import PlansLandingPage      from "../tenant-admin/PlansLandingPage.jsx";
import PlanDetailView        from "../tenant-admin/PlanDetailView.jsx";
import PlanAssignmentManager from "../tenant-admin/PlanAssignmentManager.jsx";
import PlanBuilder           from "../tenant-admin/PlanBuilder.jsx";
// S694 — TemplateDiffView: Template Difference Engine, CA-2 acknowledge mode (AD-68)
import TemplateDiffView      from "../tenant-admin/TemplateDiffView.jsx";
// S737 Step 8 — CHANGE-15: PlanComponentReviewPage — CA-2 full-page review route (AD-207)
import PlanComponentReviewPage from "./PlanComponentReviewPage.jsx";

const API_BASE    = "/api/v1";
const SESSION_KEY = "comp-workspace-tab";
const VALID_TABS  = ["plans", "templates", "components", "assignments", "approvals", "history"];

// ---------------------------------------------------------------------------
// useFetch — matches CompAdminShell pattern exactly
// ---------------------------------------------------------------------------
function useFetch(url) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!url) { setLoading(false); return; }
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Primitives — match CompAdminShell exactly
// ---------------------------------------------------------------------------
function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
  { key: "plans",       label: "Plans",       icon: FileCheck2   },
  { key: "templates",   label: "Plan Library", icon: Sparkles     },
  { key: "components",  label: "Components",  icon: Layers3      },
  { key: "assignments", label: "Assignments", icon: Workflow     },
  { key: "approvals",   label: "Approvals",   icon: CheckCircle2 },
  { key: "history",     label: "History",     icon: History      },
];

// ---------------------------------------------------------------------------
// HealthBadge — §6 Population Analysis health signal
// green pill: healthy | amber pill: attention_required | grey pill: unassigned
// ---------------------------------------------------------------------------
function HealthBadge({ status }) {
  if (!status) return null;

  const cfg = {
    healthy: {
      icon: ShieldCheck,
      label: "Healthy",
      cls: "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]",
      iconCls: "text-[rgb(var(--color-success))]",
    },
    attention_required: {
      icon: ShieldAlert,
      label: "Attention Required",
      cls: "border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]",
      iconCls: "text-[rgb(var(--color-warning))]",
    },
    unassigned: {
      icon: Clock,
      label: "Unassigned",
      cls: "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]",
      iconCls: "text-[rgb(var(--color-text-muted))]",
    },
  };

  const { icon: Icon, label, cls, iconCls } = cfg[status] ?? cfg.unassigned;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-tok-xs font-medium ${cls}`}>
      <Icon className={`h-3 w-3 ${iconCls}`} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// PopulationAnalysisPanel — §6
// GET /api/v1/plan-assignments/analyze?fiscal_period_id={id}
// Fires on tab load. Displays per-component summary.
// §6 field vocabulary: matched, already_assigned, inactive, pending_hire,
//   needs_review, ready, assigned_count, quota_coverage, on_hold_count,
//   health_status — all sourced from spec §6 table language.
// ---------------------------------------------------------------------------
function PopulationAnalysisPanel({ fiscalPeriodId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    if (!fiscalPeriodId) return;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/plan-assignments/analyze?fiscal_period_id=${fiscalPeriodId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); });
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message ?? "Unable to load population analysis."))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId]);

  useEffect(() => { load(); }, [load]);

  // §6 spec — top-level summary fields
  // Backend returns these under a top-level summary object or flat — handle both
  const summary    = data?.summary ?? data ?? null;
  const components = data?.components ?? data?.plan_components ?? [];
  const hasData    = summary || components.length > 0;

  return (
    <Card
      title="Population Analysis"
      action={
        <button
          onClick={load}
          disabled={loading || !fiscalPeriodId}
          className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-40 disabled:no-underline"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      }
    >
      {/* No fiscal period */}
      {!fiscalPeriodId && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 text-center">
          <Info className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Select a fiscal period to run population analysis.
          </p>
        </div>
      )}

      {fiscalPeriodId && loading && <LoadingRow />}
      {fiscalPeriodId && error   && <ErrorNote message={error} />}

      {/* §6 — "0 Conflicts Must Explain Itself" principle applies here too:
          prove the system checked everything on empty result */}
      {fiscalPeriodId && !loading && !error && !hasData && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-5 text-center">
          <ShieldCheck className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No assignment data found for this period.</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Analysis completed — no plan assignments exist for this fiscal period yet.
          </p>
        </div>
      )}

      {fiscalPeriodId && !loading && !error && hasData && (
        <div className="space-y-4">
          {/* Top-level population summary — §6 "Replace simple count" table */}
          {summary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { label: "Matched",          value: summary.matched          ?? summary.total_matched          ?? "—" },
                { label: "Already Assigned", value: summary.already_assigned ?? summary.total_already_assigned ?? "—" },
                { label: "Inactive",         value: summary.inactive         ?? summary.total_inactive         ?? "—" },
                { label: "Pending Hire",     value: summary.pending_hire     ?? summary.total_pending_hire     ?? "—" },
                { label: "Needs Review",     value: summary.needs_review     ?? summary.total_needs_review     ?? "—" },
                { label: "Ready",            value: summary.ready            ?? summary.total_ready            ?? "—" },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3 text-center"
                >
                  <p className="text-[18px] font-semibold text-[rgb(var(--color-text))]">{value}</p>
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Per-component breakdown — §6 per-component summary */}
          {components.length > 0 && (
            <div className="space-y-2">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                By Plan Component
              </p>
              <div className="overflow-x-auto overflow-y-auto max-h-72 rounded-xl border border-[rgb(var(--color-border))]">
                <table className="w-full text-tok-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                      <th className="px-4 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Plan / Component</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Assigned</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Quota Coverage</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">On Hold</th>
                      <th className="px-4 py-2.5 text-left  text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Health</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {components.map((comp, idx) => (
                      <tr
                        key={comp.plan_component_id ?? comp.component_id ?? idx}
                        className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-[rgb(var(--color-text))]">
                            {comp.plan_name ?? comp.plan_code ?? `Plan ${comp.plan_id ?? "—"}`}
                          </p>
                          {comp.component_label && (
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{comp.component_label}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[rgb(var(--color-text))]">
                          {comp.assigned_count ?? comp.total_assigned ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {comp.quota_coverage != null ? (
                            <span className={`font-mono ${comp.quota_coverage === 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text))]"}`}>
                              {comp.quota_coverage}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[rgb(var(--color-text))]">
                          {comp.on_hold_count ?? comp.positions_on_hold ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          <HealthBadge status={comp.health_status ?? comp.health} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ConflictRow — single conflict item, §6 operational language
// severity: "error" → red | "warning" → amber
// Fields from §6: conflict_type, conflict_category, severity, description,
//   resolution_text (recommended resolution), affected_positions
// ---------------------------------------------------------------------------
function ConflictRow({ conflict }) {
  const isError = conflict.severity === "error";

  return (
    <div className={`rounded-lg border px-4 py-3 ${
      isError
        ? "border-[rgb(var(--color-danger))]/25 bg-[rgb(var(--color-danger))]/5"
        : "border-[rgb(var(--color-warning))]/25 bg-[rgb(var(--color-warning))]/5"
    }`}>
      <div className="flex items-start gap-3">
        {isError
          ? <XCircle      className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-danger))]"  />
          : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
        }
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`text-tok-sm font-semibold ${isError ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-warning))]"}`}>
              {/* §6: conflict expressed in operational language — never raw technical codes */}
              {conflict.conflict_type_label
                ?? conflict.conflict_type?.replace(/_/g, " ")
                ?? conflict.category?.replace(/_/g, " ")
                ?? "Conflict Detected"}
            </p>
            {conflict.affected_positions != null && conflict.affected_positions > 0 && (
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {conflict.affected_positions} position{conflict.affected_positions !== 1 ? "s" : ""} affected
              </span>
            )}
          </div>

          {conflict.description && (
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {conflict.description}
            </p>
          )}

          {/* §6: Resolution text shown per conflict */}
          {(conflict.resolution_text ?? conflict.recommended_resolution) && (
            <p className={`mt-1.5 text-tok-xs font-medium ${
              isError ? "text-[rgb(var(--color-danger))]/80" : "text-[rgb(var(--color-warning))]/80"
            }`}>
              Resolution: {conflict.resolution_text ?? conflict.recommended_resolution}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConflictDetectionPanel — §6
// GET /api/v1/plan-assignments/conflicts?fiscal_period_id={id}
// On-demand: "Check for Conflicts" button.
// Groups conflicts by category (15 categories per §6).
// Errors (red) vs Warnings (amber). Resolution text per conflict.
// Total counts in header. Static placeholder on empty.
// ---------------------------------------------------------------------------
function ConflictDetectionPanel({ fiscalPeriodId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [checked, setChecked] = useState(false);

  function runCheck() {
    if (!fiscalPeriodId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setChecked(false);
    fetch(`${API_BASE}/plan-assignments/conflicts?fiscal_period_id=${fiscalPeriodId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); });
        return r.json();
      })
      .then((d) => { setData(d); setChecked(true); })
      .catch((e) => { setError(e.message ?? "Unable to check for conflicts."); setChecked(true); })
      .finally(() => setLoading(false));
  }

  // §6 response shape: conflicts may be a flat list or grouped by category
  // Backend description: "Returns conflicts grouped by category"
  // Support both shapes: { conflicts: [...] } or { categories: [...] }
  const rawConflicts    = data?.conflicts ?? data?.conflict_list ?? [];
  const rawCategories   = data?.categories ?? data?.conflict_categories ?? [];
  const totalErrors     = data?.total_errors   ?? data?.error_count   ?? rawConflicts.filter((c) => c.severity === "error").length;
  const totalWarnings   = data?.total_warnings ?? data?.warning_count ?? rawConflicts.filter((c) => c.severity === "warning").length;

  // Normalise to grouped-by-category list for display
  // If server already groups, use that; if flat list, group client-side
  const grouped = rawCategories.length > 0
    ? rawCategories
    : (() => {
        const map = {};
        rawConflicts.forEach((c) => {
          const cat = c.conflict_category ?? c.category ?? "Other";
          if (!map[cat]) map[cat] = { category: cat, conflicts: [] };
          map[cat].conflicts.push(c);
        });
        return Object.values(map);
      })();

  const hasConflicts = grouped.length > 0;

  return (
    <Card
      title="Conflict Detection"
      action={
        checked && (totalErrors + totalWarnings) > 0 ? (
          <div className="flex items-center gap-2">
            {totalErrors > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-2.5 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
                <XCircle className="h-3 w-3" />{totalErrors} error{totalErrors !== 1 ? "s" : ""}
              </span>
            )}
            {totalWarnings > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-2.5 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
                <AlertTriangle className="h-3 w-3" />{totalWarnings} warning{totalWarnings !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        ) : null
      }
    >
      <p className="mb-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Runs 15-category deterministic conflict detection across all plan assignments
        for this fiscal period. No AI — all detection is rule-based.
      </p>

      {/* Check button */}
      <div className="mb-4">
        <button
          onClick={runCheck}
          disabled={loading || !fiscalPeriodId}
          className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading
            ? <RefreshCw className="h-4 w-4 animate-spin text-[rgb(var(--color-text-muted))]" />
            : <ShieldAlert className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          }
          {loading ? "Checking…" : "Check for Conflicts"}
        </button>
        {!fiscalPeriodId && (
          <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-warning))]">
            Select a fiscal period to enable conflict detection.
          </p>
        )}
      </div>

      {error && <ErrorNote message={error} />}

      {/* Pre-check placeholder — §6: "static placeholder if API returns empty" */}
      {!checked && !loading && !error && (
        <div className="rounded-lg border border-dashed border-[rgb(var(--color-border))] px-4 py-5 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Click "Check for Conflicts" to run the 15-category validation.
          </p>
        </div>
      )}

      {/* §6 — "0 Conflicts Must Explain Itself" — show what was checked */}
      {checked && !loading && !error && !hasConflicts && (
        <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-5">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
            <div>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                No conflicts detected
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">
                All 15 conflict categories checked — 0 errors, 0 warnings.
                Population is clear for assignment commit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Grouped conflict display — §6: grouped by category, errors then warnings */}
      {checked && !loading && hasConflicts && (
        <div className="space-y-5">
          {grouped.map((group, gIdx) => {
            const groupConflicts = group.conflicts ?? [];
            const groupLabel     = group.category_label
              ?? group.category?.replace(/_/g, " ")
              ?? `Category ${gIdx + 1}`;
            const groupErrors   = groupConflicts.filter((c) => c.severity === "error").length;
            const groupWarnings = groupConflicts.filter((c) => c.severity === "warning").length;

            return (
              <div key={group.category ?? gIdx}>
                {/* Category header */}
                <div className="mb-2 flex items-center gap-2">
                  <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    {groupLabel}
                  </p>
                  {groupErrors > 0 && (
                    <span className="text-tok-xs text-[rgb(var(--color-danger))]">
                      {groupErrors} error{groupErrors !== 1 ? "s" : ""}
                    </span>
                  )}
                  {groupWarnings > 0 && (
                    <span className="text-tok-xs text-[rgb(var(--color-warning))]">
                      {groupWarnings} warning{groupWarnings !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Errors first, then warnings within each category */}
                <div className="space-y-2">
                  {[...groupConflicts]
                    .sort((a, b) => (a.severity === "error" ? -1 : 1) - (b.severity === "error" ? -1 : 1))
                    .map((conflict, cIdx) => (
                      <ConflictRow key={conflict.conflict_id ?? cIdx} conflict={conflict} />
                    ))
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// PlanApprovalPanel — S586 rebuild
// OI-APPROVALS-TAB-COMPONENT-FILTER: replaced card view with sortable,
// searchable, scrollable table. Click-through opens type-aware detail view.
// Filter bug fixed: components whose parent plan is approved are excluded.
// Badge count fixed in main workspace scope (see approvalCount below).
// Add to Library surfaced as post-approval next action.
// ---------------------------------------------------------------------------

// Modifier type → human-readable plan type label
const MODIFIER_TYPE_LABEL = {
  "P3B-DRAW":    "Draw",
  "P3B-SPLIT":   "Deal Split",
  "P3B-SPIFF":   "Spiff",
  "P3B-KICKER":  "Kicker",
  "P3B-TXN":     "Transaction",
  "P3B-REVTYPE": "Rev Type",
  "P3B-CAP":     "Cap",
  "P3B-CLAWBACK":"Clawback",
};

function derivePlanType(modifiers = []) {
  const required = modifiers.find((m) => m.applicability === "required");
  if (!required) return "ARR Quota";
  return MODIFIER_TYPE_LABEL[required.modifier_type] ?? required.modifier_type;
}

// ── Component Detail Drawer ──────────────────────────────────────────────────
// Fetches GET /plan-components/{id} (with modifiers) and renders type-aware panel.
// Approve action lives here. Add to Library offered post-approval.

function ComponentDetailDrawer({
  comp,
  planMap,
  roleMap,
  segmentMap,
  fiscalPeriodId,
  wizardSessionId,
  onClose,
  onApproved,
}) {
  // OI-ACTOR-SEPARATION-GLOBAL: need current user to gate self-approval
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.user_account_id ?? null;

  const [detail,       setDetail]       = useState(null);
  const [detailLoading,setDetailLoading]= useState(true);
  const [detailError,  setDetailError]  = useState(null);
  const [amendOpen,    setAmendOpen]    = useState(false);
  const [actionError,  setActionError]  = useState("");
  const [actionSuccess,setActionSuccess]= useState("");
  const [actioning,    setActioning]    = useState(false);
  const [addingLib,    setAddingLib]    = useState(false);
  const [libSuccess,   setLibSuccess]   = useState("");
  const [libError,     setLibError]     = useState("");

  // S694 — Template Difference Engine: CA-2 acknowledge mode.
  // Fetch delta when parent plan is a PATH A (library-deployed) plan.
  // plan.template_id presence is the PATH A signal — same as PlanDetailView (AD-72).
  // deltaAcked: tracks whether TemplateDiffView checkbox has been checked this session.
  // When has_delta=false the checkbox is not shown and deltaAcked is irrelevant.
  const [deltaData,    setDeltaData]    = useState(null);
  const [deltaLoading, setDeltaLoading] = useState(false);
  const [deltaError,   setDeltaError]   = useState(null);
  const [deltaAcked,   setDeltaAcked]   = useState(false);

  const plan    = planMap[String(comp?.plan_id)];

  useEffect(() => {
    // Fetch delta for PATH A plans only (plan.template_id present).
    // Non-blocking: failure never blocks the approval flow.
    if (!plan?.template_id || !comp?.plan_id) {
      setDeltaData(null);
      setDeltaError(null);
      setDeltaLoading(false);
      return;
    }
    setDeltaLoading(true);
    setDeltaError(null);
    setDeltaAcked(false); // reset checkbox each time drawer opens for a new component
    fetch(`${API_BASE}/template-library/deltas/by-plan/${comp.plan_id}`, {
      credentials: "include",
    })
      .then((r) => {
        if (r.status === 404) { setDeltaData(null); return; } // no delta yet — not an error
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => { if (data) setDeltaData(data); })
      .catch(() => setDeltaError("Unable to load template comparison."))
      .finally(() => setDeltaLoading(false));
  }, [comp?.plan_id, plan?.template_id]); // eslint-disable-line

  useEffect(() => {
    if (!comp) return;
    setDetailLoading(true);
    setDetailError(null);
    fetch(`${API_BASE}/plan-components/${comp.plan_component_id}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setDetail)
      .catch((e) => setDetailError(e.message))
      .finally(() => setDetailLoading(false));
  }, [comp]);

  const role    = roleMap[String(comp?.role_id)];
  const segment = segmentMap[String(comp?.segment_id)];
  const planType = detail ? derivePlanType(detail.modifiers) : "—";
  const isPlanApproved = plan?.plan_status === "approved";

  async function handleAmendmentBegin(amendmentId) {
    setAmendOpen(false);
    setActioning(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/plan-components/${comp.plan_component_id}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amendment_id: amendmentId, fiscal_period_id: fiscalPeriodId }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      }).catch(() => {});
      setActionSuccess("Component approved. It is now engine-eligible.");
      onApproved();
    } catch (e) {
      setActionError(e.message ?? "Unable to approve component.");
    } finally {
      setActioning(false);
    }
  }

  // OI-FIRST-APPROVAL-GATE-REGRESSION (S700)
  // First-time approval: approved_by IS NULL → no amendment exists yet, none needed.
  // Amendment gate (DR-007) governs mutations to already-approved artifacts, not
  // initial approval of a draft component. Call /approve directly — backend bypass
  // already correct per AD-66 (S691). AmendmentBeginModal must NOT open here.
  async function handleDirectApprove() {
    setActioning(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/plan-components/${comp.plan_component_id}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      setActionSuccess("Component approved. It is now engine-eligible.");
      onApproved();
    } catch (e) {
      setActionError(e.message ?? "Unable to approve component.");
    } finally {
      setActioning(false);
    }
  }

  async function handleAddToLibrary() {
    if (!plan?.plan_id) { setLibError("No parent plan found."); return; }
    setAddingLib(true); setLibError(""); setLibSuccess("");
    try {
      const res = await fetch(`${API_BASE}/template-library/templates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: plan.plan_id }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      setLibSuccess("Plan added to template library.");
    } catch (e) {
      setLibError(e.message ?? "Unable to add to library.");
    } finally {
      setAddingLib(false);
    }
  }

  return (
    <>
      <AmendmentBeginModal
        open={amendOpen}
        entityType="plan_component"
        entityId={comp ? String(comp.plan_component_id) : null}
        fiscalPeriodId={fiscalPeriodId}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={() => setAmendOpen(false)}
      />

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col bg-[rgb(var(--color-surface))] shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-6 py-4">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Component #{comp?.plan_component_id}</p>
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              {plan?.plan_name ?? plan?.plan_code ?? `Plan ${comp?.plan_id}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {detailLoading && <LoadingRow />}
          {detailError && <ErrorNote message="Unable to load component details." />}

          {!detailLoading && !detailError && detail && (
            <>
              {/* S30.5 — PCMA Gate 2 advisory banner.
                  Shown when component has no modifier applicability rows — approval will be
                  blocked by the backend Gate 2 check. Amber, non-dismissible, non-blocking.
                  CA-2 can still review the component but Approve button is disabled below. */}
              {detail.modifiers?.length === 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                  <div>
                    <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
                      Plan type not configured
                    </p>
                    <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-warning))]/80">
                      This component has no plan type configured. Approval is blocked until
                      set-plan-type is called for this component.
                    </p>
                  </div>
                </div>
              )}

              {/* Plan type badge */}
              <div className="flex items-center gap-2">
                <Pill tone={isPlanApproved ? "Success" : "Warning"}>{planType}</Pill>
                <Pill tone={detail.approved_by ? "Success" : "Warning"}>
                  {detail.approved_by ? "Approved" : "Pending Approval"}
                </Pill>
                {isPlanApproved && (
                  <Pill tone="Success">Plan Approved</Pill>
                )}
              </div>

              {/* Core fields — always shown */}
              <section>
                <h3 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plan Context</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Plan"      value={plan?.plan_name ?? plan?.plan_code ?? `Plan ${comp.plan_id}`} />
                  <Field label="Version"   value={plan?.version != null ? `v${plan.version}` : "—"} />
                  <Field label="Role"      value={role?.role_name ?? role?.role_code ?? `Role ${comp.role_id}`} />
                  <Field label="Segment"   value={segment?.segment_name ?? segment?.segment_code ?? `Seg ${comp.segment_id}`} />
                  <Field label="Period"    value={plan?.fiscal_period_id ?? "—"} />
                  <Field label="Effective" value={detail.effective_date ?? "—"} />
                </div>
              </section>

              {/* OTE & split — shown for all types */}
              <section>
                <h3 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">OTE Structure</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="OTE Min"      value={detail.ote_min != null ? `$${Number(detail.ote_min).toLocaleString()}` : "—"} />
                  <Field label="OTE Midpoint" value={detail.ote_midpoint != null ? `$${Number(detail.ote_midpoint).toLocaleString()}` : "—"} />
                  <Field label="OTE Max"      value={detail.ote_max != null ? `$${Number(detail.ote_max).toLocaleString()}` : "—"} />
                  <Field label="Currency"     value={detail.ote_currency_code ?? "—"} />
                  <Field label="Base %"       value={detail.base_pct != null ? `${Math.round(detail.base_pct * 100)}%` : "—"} />
                  <Field label="Variable %"   value={detail.variable_pct != null ? `${Math.round(detail.variable_pct * 100)}%` : "—"} />
                </div>
              </section>

              {/* Component weights — ARR / quota types */}
              {(planType === "ARR Quota" || planType === "Rev Type") && (
                <section>
                  <h3 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">Component Weights</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="ARR Weight"      value={detail.arr_weight ?? "—"} />
                    <Field label="MBO Weight"      value={detail.mbo_weight ?? "—"} />
                    <Field label="NPS Weight"      value={detail.nps_weight ?? "—"} />
                    <Field label="Adoption Weight" value={detail.adoption_weight ?? "—"} />
                  </div>
                </section>
              )}

              {/* Modifier applicability — shown for all non-baseline types */}
              {detail.modifiers?.length > 0 && planType !== "ARR Quota" && (
                <section>
                  <h3 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plan Modifiers</h3>
                  <div className="overflow-hidden rounded-lg border border-[rgb(var(--color-border))]">
                    <table className="w-full text-tok-sm">
                      <thead className="bg-[rgb(var(--color-surface-muted))]">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))]">Modifier</th>
                          <th className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))]">Applicability</th>
                          <th className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))]">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[rgb(var(--color-border))]">
                        {detail.modifiers.map((m) => (
                          <tr key={m.applicability_id} className="bg-[rgb(var(--color-surface))]">
                            <td className="px-3 py-2 font-mono text-tok-xs text-[rgb(var(--color-text))]">{m.modifier_type}</td>
                            <td className="px-3 py-2">
                              <Pill tone={m.applicability === "required" ? "Warning" : m.applicability === "optional" ? "Info" : "Default"}>
                                {m.applicability}
                              </Pill>
                            </td>
                            <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">{m.modifier_status ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Approval trail */}
              <section>
                <h3 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">Approval Trail</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Created By"  value={detail.created_by ?? "—"} />
                  <Field label="Created At"  value={detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"} />
                  <Field label="Approved By" value={detail.approved_by ?? "Pending"} />
                  <Field label="Approved At" value={detail.approved_at ? new Date(detail.approved_at).toLocaleString() : "—"} />
                </div>
              </section>

              {/* S694 — Template Difference Engine: CA-2 acknowledge mode.
                  Shown for PATH A plans only (plan.template_id present).
                  AD-68: same TemplateDiffView component, mode='acknowledge'.
                  NN-36: SoD enforced at DB level (chk_delta_sod).
                  Checkbox gates Approve button below via deltaAcked state.
                  When has_delta=false: NoDeltaBanner shown, no checkbox needed,
                  deltaAcked is irrelevant — Approve button not blocked. */}
              {plan?.template_id && (
                <TemplateDiffView
                  mode="acknowledge"
                  deltaData={deltaData}
                  loading={deltaLoading}
                  error={deltaError}
                  onAcknowledgedChange={(acked) => setDeltaAcked(acked)}
                />
              )}

              {/* Success / error feedback */}
              {actionSuccess && (
                <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                  {actionSuccess}
                </div>
              )}
              {actionError && <ErrorNote message={actionError} />}
              {libSuccess && (
                <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                  {libSuccess}
                </div>
              )}
              {libError && <ErrorNote message={libError} />}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[rgb(var(--color-border))] px-6 py-4 flex items-center gap-3 flex-wrap">
          {!detail?.approved_by && !actionSuccess && (() => {
            // OI-ACTOR-SEPARATION-GLOBAL: disable approve when current user created this component
            const compCreatedBy = detail?.created_by || null;
            const isSelfApproval = !!(currentUserId && compCreatedBy && String(currentUserId) === String(compCreatedBy));

            // S694 — Template Difference Engine: gate Approve when PATH A plan has
            // unacknowledged deviations. Rule: block only when all three are true:
            //   1. plan is PATH A (template_id present)
            //   2. delta exists and has changes (has_delta=true)
            //   3. checkbox not yet checked (deltaAcked=false) AND not already acked on server
            const serverAcked = deltaData?.delta_acknowledged ?? false;
            const deltaBlocksApprove = !!(
              plan?.template_id &&
              deltaData?.has_delta &&
              !serverAcked &&
              !deltaAcked
            );

            // S30.5 — PCMA Gate 2: block Approve when component has no applicability rows.
            // Mirrors backend Gate 2 check. No PCMA rows = plan type not configured.
            const pcmaGate2Blocked = detail?.modifiers?.length === 0;

            return (
              <>
                {isSelfApproval && (
                  <p className="w-full flex items-center gap-2 rounded border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                    <span>⚠</span>
                    You submitted this — a second comp admin must approve it.
                  </p>
                )}
                {deltaBlocksApprove && (
                  <p className="w-full flex items-center gap-2 rounded border border-[rgb(var(--color-info))]/40 bg-[rgb(var(--color-info))]/10 px-3 py-2 text-tok-xs text-[rgb(var(--color-info))]">
                    <span>ℹ</span>
                    Acknowledge the template deviations above to enable approval.
                  </p>
                )}
                <Button
                  onClick={() => {
                    if (!fiscalPeriodId) { setActionError("No active fiscal period — cannot approve."); return; }
                    setActionError("");
                    handleDirectApprove();
                  }}
                  disabled={actioning || isSelfApproval || deltaBlocksApprove || pcmaGate2Blocked}
                >
                  {actioning ? "Approving…" : "Approve Component"}
                </Button>
              </>
            );
          })()}
          {(detail?.approved_by || actionSuccess) && isPlanApproved && !libSuccess && (
            <Button
              variant="secondary"
              onClick={handleAddToLibrary}
              disabled={addingLib}
            >
              {addingLib ? "Adding…" : "Add to Template Library"}
            </Button>
          )}
          <button
            onClick={onClose}
            className="ml-auto text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ModifierHistorySection — S731 OI-APQ-HISTORY
// GET /api/v1/approvals/modifier-history
// Collapsible section showing resolved modifier approvals (approved + rejected).
// Read-only. Sortable by type, outcome, date. Searchable by name/plan/actor.
// Renders as Section C inside PlanApprovalPanel.
// ---------------------------------------------------------------------------
function ModifierHistorySection({ open, onToggle }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [sortKey, setSortKey] = useState("actioned_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/approvals/modifier-history`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`Error ${r.status}`); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message ?? "Unable to load approval history."))
      .finally(() => setLoading(false));
  }, []);

  // Load once on mount
  useEffect(() => { load(); }, [load]);

  const history = data?.history ?? [];

  // Search across modifier_name, plan_name, actor_name, modifier_label
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return history;
    return history.filter((h) =>
      (h.modifier_name  ?? "").toLowerCase().includes(q) ||
      (h.plan_name      ?? "").toLowerCase().includes(q) ||
      (h.actor_name     ?? "").toLowerCase().includes(q) ||
      (h.modifier_label ?? "").toLowerCase().includes(q)
    );
  }, [history, search]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "modifier_type": av = a.modifier_label ?? ""; bv = b.modifier_label ?? ""; break;
        case "outcome":       av = a.outcome ?? "";        bv = b.outcome ?? "";        break;
        case "actioned_at":   av = a.actioned_at ?? "";   bv = b.actioned_at ?? "";    break;
        case "actor_name":    av = a.actor_name ?? "";     bv = b.actor_name ?? "";     break;
        default:              av = a[sortKey] ?? "";       bv = b[sortKey] ?? "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  function SortIcon({ col }) {
    if (sortKey !== col) return <ChevronUp className="h-3 w-3 opacity-20" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="h-3 w-3 text-[rgb(var(--color-primary))]" />;
  }

  function formatRelativeDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7)  return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  const thCls = "px-3 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide whitespace-nowrap select-none cursor-pointer hover:text-[rgb(var(--color-text))]";
  const tdCls = "px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap";

  return (
    <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Section header — always visible, clickable to collapse */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left focus:outline-none"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Approval History
          </h3>
          {!loading && history.length > 0 && (
            <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))]
                             bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs
                             text-[rgb(var(--color-text-muted))]">
              {history.length} decision{history.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); load(); }}
            disabled={loading}
            className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1
                       text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]
                       disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          {open
            ? <ChevronUp   className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            : <ChevronDown className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          }
        </div>
      </button>

      {open && (
        <div className="border-t border-[rgb(var(--color-border))]">
          {/* Search bar */}
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, plan, actor…"
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-9 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {loading ? "Loading…" : `${sorted.length} of ${history.length}`}
              {search ? " (filtered)" : ""}
            </p>
          </div>

          {error && <div className="px-4 pb-4"><ErrorNote message={error} /></div>}

          {!loading && !error && history.length === 0 && (
            <div className="px-4 pb-6 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--color-text-muted))]" />
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No approval decisions recorded yet.
              </p>
            </div>
          )}

          {!error && history.length > 0 && (
            <div className="overflow-x-auto">
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                    <tr>
                      <th className={thCls} onClick={() => toggleSort("modifier_type")}>
                        <span className="flex items-center gap-1">Type <SortIcon col="modifier_type" /></span>
                      </th>
                      <th className={thCls}>Name</th>
                      <th className={thCls}>Plan</th>
                      <th className={thCls} onClick={() => toggleSort("outcome")}>
                        <span className="flex items-center gap-1">Outcome <SortIcon col="outcome" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("actor_name")}>
                        <span className="flex items-center gap-1">Actioned By <SortIcon col="actor_name" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("actioned_at")}>
                        <span className="flex items-center gap-1">Date <SortIcon col="actioned_at" /></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {sorted.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                          No decisions match your search.
                        </td>
                      </tr>
                    ) : sorted.map((item, idx) => (
                      <tr
                        key={`${item.outcome}-${item.modifier_type}-${item.modifier_id ?? idx}`}
                        className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                      >
                        {/* Type */}
                        <td className={tdCls}>
                          <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))]
                                           bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs
                                           text-[rgb(var(--color-text-muted))]">
                            {item.modifier_label}
                          </span>
                        </td>
                        {/* Name */}
                        <td className={tdCls + " max-w-[140px] truncate font-medium"}>
                          {item.modifier_name ?? <span className="italic text-[rgb(var(--color-text-muted))]">—</span>}
                        </td>
                        {/* Plan */}
                        <td className={tdCls + " max-w-[140px] truncate text-[rgb(var(--color-text-muted))]"}>
                          {item.plan_name ?? <span className="italic">—</span>}
                        </td>
                        {/* Outcome pill */}
                        <td className={tdCls}>
                          {item.outcome === "approved" ? (
                            <span className="inline-flex items-center gap-1 rounded-full border
                                             border-[rgb(var(--color-success))]/30
                                             bg-[rgb(var(--color-success))]/10
                                             px-2 py-0.5 text-tok-xs font-medium
                                             text-[rgb(var(--color-success))]">
                              <CheckCircle className="h-3 w-3" />
                              Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border
                                             border-[rgb(var(--color-warning))]/30
                                             bg-[rgb(var(--color-warning))]/10
                                             px-2 py-0.5 text-tok-xs font-medium
                                             text-[rgb(var(--color-warning))]">
                              <AlertTriangle className="h-3 w-3" />
                              Returned
                            </span>
                          )}
                        </td>
                        {/* Actor */}
                        <td className={tdCls + " text-[rgb(var(--color-text-muted))]"}>
                          {item.actor_name ?? (item.actor ? item.actor.slice(0, 8) + "…" : "—")}
                        </td>
                        {/* Date — relative with full timestamp on hover */}
                        <td className={tdCls}>
                          <span
                            title={item.actioned_at ? new Date(item.actioned_at).toLocaleString() : ""}
                            className="cursor-default text-[rgb(var(--color-text-muted))]"
                          >
                            {formatRelativeDate(item.actioned_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// OI-APQ-03 S733 — Modifier type display labels for Section B filter dropdown.
// Mirrors _MODIFIER_LABEL in approvals_router.py — kept in sync manually.
const _MODIFIER_LABEL = {
  kicker:      "Kicker",
  draw:        "Draw",
  spiff:       "SPIFF",
  clawback:    "Clawback",
  revtype:     "RevType",
  split:       "Split",
  transaction: "Transaction",
};

// ── PlanApprovalPanel ────────────────────────────────────────────────────────

const COMP_SORT_KEYS = ["plan_component_id", "plan", "role", "segment", "type", "ote_min", "effective_date", "status"];

function PlanApprovalPanel({ mode, period, wizardSessionId }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;
  const { user } = useAuth();  // OI-PLAN-LIBRARY-SUBMIT-BUTTONS S644 — needed for submitter gate
  const currentUserId = user?.user_account_id ?? null;  // CHANGE-08 S725: derive once — used by plan rows + modifier rows

  // Data fetches
  const { data: compData,        loading: compLoading,  error: compError  } = useFetch(`${API_BASE}/plan-components`);
  const { data: plansData }    = useFetch(`${API_BASE}/plans`);
  const { data: rolesData }    = useFetch(`${API_BASE}/roles`);
  const { data: segmentsData } = useFetch(`${API_BASE}/segments`);
  const { data: planApprovalData, loading: planLoading, error: planError } = useFetch(`${API_BASE}/plans?plan_status=pending_approval`);

  const allComponents = compData?.plan_components ?? compData?.data ?? [];
  const allPlans      = plansData?.plans    ?? plansData?.data    ?? [];
  const allRoles      = rolesData?.roles    ?? rolesData?.data    ?? [];
  const allSegments   = segmentsData?.segments ?? segmentsData?.data ?? [];
  const pendingPlans  = planApprovalData?.data ?? planApprovalData?.plans ?? [];

  // Lookup maps — declared before any filter that uses them
  const planMap    = Object.fromEntries(allPlans.map((p) => [String(p.plan_id), p]));
  const roleMap    = Object.fromEntries(allRoles.map((r) => [String(r.role_id), r]));
  const segmentMap = Object.fromEntries(allSegments.map((s) => [String(s.segment_id), s]));

  // Refresh after approve
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: freshData } = useFetch(refreshKey > 0 ? `${API_BASE}/plan-components?_k=${refreshKey}` : null);
  const [localComponents, setLocalComponents] = useState(null);
  useEffect(() => {
    if (freshData) setLocalComponents(freshData?.plan_components ?? freshData?.data ?? []);
  }, [freshData]);

  // FIX: exclude components whose parent plan is already approved
  const baseComponents = localComponents ?? allComponents;
  const pendingComponents = baseComponents.filter(
    (c) => !c.approved_by && !c.approved_at && planMap[String(c.plan_id)]?.plan_status !== "approved"
  );

  // Table state
  const [search,     setSearch]     = useState("");
  const [sortKey,    setSortKey]    = useState("plan_component_id");
  const [sortDir,    setSortDir]    = useState("asc");
  const [selectedComp, setSelectedComp] = useState(null);

  // Plan-level approval state
  const [rejectingPlanId, setRejectingPlanId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [planActionError, setPlanActionError] = useState("");
  const [planActioning,   setPlanActioning]   = useState(false);

  // S731 — Section collapse state. A (components) open by default; B and C collapsed.
  // Independent per section so users can focus on the queue they care about.
  const [sectionAOpen, setSectionAOpen] = useState(true);
  const [sectionBOpen, setSectionBOpen] = useState(true);
  const [sectionCOpen, setSectionCOpen] = useState(false);

  // OI-APQ-01 S730: Modifier approvals queue — single endpoint, all 7 types, no N×7 fetch.
  // GET /api/v1/approvals/pending-modifiers returns approve_url + reject_url per item.
  // plan_name resolved server-side via plan_component → plan JOIN.
  const [modifierApprovals,        setModifierApprovals]        = useState([]);
  const [modifierApprovalsLoading, setModifierApprovalsLoading] = useState(false);
  const [modifierLoadErrors,       setModifierLoadErrors]       = useState([]);
  const [modifierRejectingId,      setModifierRejectingId]      = useState(null); // "type-id" key
  const [modifierRejectReason,     setModifierRejectReason]     = useState("");

  // OI-APQ-03 S733: Queue filter state — modifier type, plan name search, date range.
  // All filtering is client-side — data already returned by pending-modifiers endpoint.
  const [modFilterType, setModFilterType] = useState("");   // '' = all types
  const [modFilterPlan, setModFilterPlan] = useState("");   // free-text plan name search
  const [modFilterFrom, setModFilterFrom] = useState("");   // submitted_at >= (ISO date string)
  const [modFilterTo,   setModFilterTo]   = useState("");   // submitted_at <= (ISO date string)

  const loadModifierApprovals = useCallback(async () => {
    setModifierApprovalsLoading(true);
    setModifierLoadErrors([]);
    try {
      const res = await fetch(`${API_BASE}/approvals/pending-modifiers`, { credentials: "include" });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setModifierApprovals(data.modifiers ?? []);
    } catch (e) {
      setModifierLoadErrors(["Failed to load modifier approvals."]);
      setModifierApprovals([]);
    } finally {
      setModifierApprovalsLoading(false);
    }
  }, []);

  useEffect(() => { loadModifierApprovals(); }, [loadModifierApprovals]);

  // OI-APQ-03 S733: Derived filtered list — client-side, all filters applied in one pass.
  // modFilterType: exact match on modifier_type ('kicker', 'draw', etc.) or '' for all.
  // modFilterPlan: case-insensitive substring match on plan_name.
  // modFilterFrom/To: submitted_at date range (ISO date strings, inclusive).
  const filteredModifierApprovals = useMemo(() => {
    return modifierApprovals.filter((item) => {
      if (modFilterType && item.modifier_type !== modFilterType) return false;
      if (modFilterPlan) {
        const q = modFilterPlan.toLowerCase();
        const planMatch = (item.plan_name ?? "").toLowerCase().includes(q) ||
                          (item.modifier_name ?? "").toLowerCase().includes(q);
        if (!planMatch) return false;
      }
      if (modFilterFrom && item.submitted_at) {
        if (item.submitted_at.slice(0, 10) < modFilterFrom) return false;
      }
      if (modFilterTo && item.submitted_at) {
        if (item.submitted_at.slice(0, 10) > modFilterTo) return false;
      }
      return true;
    });
  }, [modifierApprovals, modFilterType, modFilterPlan, modFilterFrom, modFilterTo]);

  const modActiveFilterCount = [modFilterType, modFilterPlan, modFilterFrom, modFilterTo]
    .filter(Boolean).length;

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  }

  function SortIcon({ col }) {
    if (sortKey !== col) return <ChevronUp className="h-3 w-3 opacity-20" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="h-3 w-3 text-[rgb(var(--color-primary))]" />;
  }

  // Build enriched rows for table display
  const enriched = pendingComponents.map((c) => ({
    ...c,
    _plan:    planMap[String(c.plan_id)],
    _role:    roleMap[String(c.role_id)],
    _segment: segmentMap[String(c.segment_id)],
  }));

  // Search across plan name, role, segment
  const searched = enriched.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c._plan?.plan_name  ?? "").toLowerCase().includes(q) ||
      (c._plan?.plan_code  ?? "").toLowerCase().includes(q) ||
      (c._role?.role_name  ?? "").toLowerCase().includes(q) ||
      (c._role?.role_code  ?? "").toLowerCase().includes(q) ||
      (c._segment?.segment_name ?? "").toLowerCase().includes(q) ||
      String(c.plan_component_id).includes(q)
    );
  });

  // Sort
  const sorted = [...searched].sort((a, b) => {
    let av, bv;
    switch (sortKey) {
      case "plan":           av = a._plan?.plan_name ?? ""; bv = b._plan?.plan_name ?? ""; break;
      case "role":           av = a._role?.role_name ?? ""; bv = b._role?.role_name ?? ""; break;
      case "segment":        av = a._segment?.segment_name ?? ""; bv = b._segment?.segment_name ?? ""; break;
      case "ote_min":        av = Number(a.ote_min ?? 0);  bv = Number(b.ote_min ?? 0);  break;
      case "effective_date": av = a.effective_date ?? "";  bv = b.effective_date ?? "";  break;
      default:               av = a[sortKey] ?? "";        bv = b[sortKey] ?? "";
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // handleApprovePlan DELETED — AD-42 violation. POST /plans/{id}/approve from UI is
  // prohibited. Plan status derives from component approval via _derive_plan_status().
  // CA-2 approves plan_components via the Approvals tab component drawer. (S724 CHANGE-03)

  async function handleReturnForChanges(planId) {
    if (!rejectionReason.trim()) { setPlanActionError("Rejection reason is required."); return; }
    setPlanActioning(true); setPlanActionError("");
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/reject`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rejection_reason: rejectionReason.trim() }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.error?.message ?? `Error ${res.status}`);
      }
      window.location.reload();
    } catch (e) { setPlanActionError(e.message); } finally { setPlanActioning(false); }
  }

  const thCls = "px-3 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide whitespace-nowrap select-none cursor-pointer hover:text-[rgb(var(--color-text))]";
  const tdCls = "px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap";

  return (
    <>
      {/* Component detail drawer */}
      {selectedComp && (
        <ComponentDetailDrawer
          comp={selectedComp}
          planMap={planMap}
          roleMap={roleMap}
          segmentMap={segmentMap}
          fiscalPeriodId={fiscalPeriodId}
          wizardSessionId={wizardSessionId}
          onClose={() => setSelectedComp(null)}
          onApproved={() => {
            setSelectedComp(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      <div className="w-full space-y-6">

        {/* ── Section A: Plan Components ── */}
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">

          {/* Section A header — collapsible */}
          <button
            type="button"
            onClick={() => setSectionAOpen((v) => !v)}
            className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--color-border))] px-4 py-3 text-left focus:outline-none"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                Plan Components Pending Approval
              </h3>
              {pendingComponents.length > 0 && (
                <Pill tone="Warning">{pendingComponents.length} pending</Pill>
              )}
            </div>
            <div className="flex items-center gap-2">
              {sectionAOpen
                ? <ChevronUp   className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                : <ChevronDown className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
              }
            </div>
          </button>

          {sectionAOpen && (
          <div>
          {/* Search bar inside collapsible body */}
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-b border-[rgb(var(--color-border))]">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plan, role, segment…"
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-9 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {compLoading && <div className="px-4 py-6"><LoadingRow /></div>}
          {compError   && <div className="px-4 py-4"><ErrorNote message="Unable to load plan components." /></div>}

          {!compLoading && !compError && (
            <div className="overflow-x-auto">
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                    <tr>
                      <th className={thCls} onClick={() => toggleSort("plan_component_id")}>
                        <span className="flex items-center gap-1">ID <SortIcon col="plan_component_id" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("plan")}>
                        <span className="flex items-center gap-1">Plan <SortIcon col="plan" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("role")}>
                        <span className="flex items-center gap-1">Role <SortIcon col="role" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("segment")}>
                        <span className="flex items-center gap-1">Segment <SortIcon col="segment" /></span>
                      </th>
                      <th className={thCls}>Type</th>
                      <th className={thCls} onClick={() => toggleSort("ote_min")}>
                        <span className="flex items-center gap-1">OTE Range <SortIcon col="ote_min" /></span>
                      </th>
                      <th className={thCls} onClick={() => toggleSort("effective_date")}>
                        <span className="flex items-center gap-1">Effective <SortIcon col="effective_date" /></span>
                      </th>
                      <th className={thCls}>Status</th>
                      <th className="px-3 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {sorted.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                          {search ? "No components match your search." : "No plan components pending approval."}
                        </td>
                      </tr>
                    )}
                    {sorted.map((c) => {
                      const planName = c._plan?.plan_name ?? c._plan?.plan_code ?? `Plan ${c.plan_id}`;
                      const roleName = c._role?.role_name ?? c._role?.role_code ?? `Role ${c.role_id}`;
                      const segName  = c._segment?.segment_name ?? c._segment?.segment_code ?? `Seg ${c.segment_id}`;
                      const oteRange = c.ote_min != null && c.ote_max != null
                        ? `$${Number(c.ote_min).toLocaleString()} – $${Number(c.ote_max).toLocaleString()}`
                        : "—";
                      return (
                        <tr
                          key={c.plan_component_id}
                          onClick={() => setSelectedComp(c)}
                          className="cursor-pointer bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                        >
                          <td className={tdCls + " font-mono text-tok-xs text-[rgb(var(--color-text-muted))]"}>#{c.plan_component_id}</td>
                          <td className={tdCls + " font-medium max-w-[180px] truncate"} title={planName}>{planName}</td>
                          <td className={tdCls + " max-w-[120px] truncate"} title={roleName}>{roleName}</td>
                          <td className={tdCls + " max-w-[120px] truncate"} title={segName}>{segName}</td>
                          <td className={tdCls}>
                            <Pill tone="Info">ARR Quota</Pill>
                          </td>
                          <td className={tdCls + " tabular-nums"}>{oteRange}</td>
                          <td className={tdCls}>{c.effective_date ?? "—"}</td>
                          <td className={tdCls}>
                            <Pill tone={c.approved_by ? "Success" : "Warning"}>
                              {c.approved_by ? "Approved" : "Pending"}
                            </Pill>
                          </td>
                          <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setSelectedComp(c)}
                              className="rounded-lg border border-[rgb(var(--color-primary))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/5"
                            >
                              Review →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
          )}
        </div>

        {/* ── Section 2: Plans Pending Approval ── */}
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
          <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plans Pending Approval</h3>
              {pendingPlans.length > 0 && <Pill tone="Warning">{pendingPlans.length} pending</Pill>}
            </div>
          </div>

          {planLoading && <div className="px-4 py-6"><LoadingRow /></div>}
          {planError   && <div className="px-4 py-4"><ErrorNote message="Unable to load plans." /></div>}
          {planActionError && <div className="px-4 pt-3"><ErrorNote message={planActionError} /></div>}

          {!planLoading && !planError && (
            <div className="overflow-x-auto">
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                    <tr>
                      <th className={thCls}>Plan</th>
                      <th className={thCls}>Code</th>
                      <th className={thCls}>Version</th>
                      <th className={thCls}>Period</th>
                      <th className={thCls}>Submitted By</th>
                      <th className="px-3 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {pendingPlans.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                          No plans pending approval.
                        </td>
                      </tr>
                    )}
                    {pendingPlans.map((plan) => (
                      <tr key={plan.plan_id} className="bg-[rgb(var(--color-surface))]">
                        <td className={tdCls + " font-medium"}>{plan.plan_name ?? `Plan ${plan.plan_id}`}</td>
                        <td className={tdCls + " font-mono text-tok-xs text-[rgb(var(--color-text-muted))]"}>{plan.plan_code ?? "—"}</td>
                        <td className={tdCls}>{plan.version != null ? `v${plan.version}` : "—"}</td>
                        <td className={tdCls}>{plan.fiscal_period_id ?? "—"}</td>
                        <td className={tdCls + " text-[rgb(var(--color-text-muted))]"}>{plan.submitted_by ?? "—"}</td>
                        <td className="px-3 py-2.5">
                          {/* OI-PLAN-LIBRARY-SUBMIT-BUTTONS S644: gate on submitted_by — submitter sees read-only state (NN-36) */}
                          {plan.submitted_by !== user?.user_account_id ? (  // S720 AD-166 — user_id → user_account_id (MeResponse field confirmed auth_service.py:818)
                          <div className="flex items-center gap-2">
                            {/* AD-42: Approve button removed — plan status derives from component approval.
                                CA-2 must approve plan_components via the component drawer (S724 CHANGE-03).
                                ⚠️ OI-FIRST-APPROVAL-GATE-REGRESSION: if component approval fails with
                                "amendment required", use Begin Amendment → Data Correction as workaround. */}
                            <button
                              onClick={() => {
                                const params = new URLSearchParams(window.location.search);
                                params.set('tab', 'plans');
                                params.set('planId', String(plan.plan_id));
                                window.location.search = params.toString();
                              }}
                              className="rounded-lg border border-[rgb(var(--color-primary))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/5"
                            >
                              View Plan →
                            </button>
                            {rejectingPlanId === plan.plan_id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  placeholder="Reason (required)"
                                  className="w-44 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                                />
                                <button
                                  onClick={() => handleReturnForChanges(plan.plan_id)}
                                  disabled={planActioning || !currentUserId || plan.submitted_by === currentUserId}
                                  className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => { setRejectingPlanId(null); setRejectionReason(""); }}
                                  className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setRejectingPlanId(plan.plan_id); setRejectionReason(""); setPlanActionError(""); }}
                                disabled={planActioning || !currentUserId || plan.submitted_by === currentUserId}
                                className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50"
                              >
                                Return for Changes
                              </button>
                            )}
                          </div>
                          ) : (
                            <span className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                              <Clock size={12} />
                              Submitted — awaiting approval
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── Section B: Modifier Approvals (CHANGE-08 / S724 approve + S725 Return for Changes) ── */}
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
          {/* Section B header — collapsible */}
          <button
            type="button"
            onClick={() => setSectionBOpen((v) => !v)}
            className="flex w-full items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3 text-left focus:outline-none"
          >
            <div className="flex items-center gap-2">
              <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                Modifier Approvals
              </h3>
              {modifierApprovals.length > 0 && (
                <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-warning))]/30
                                 bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium
                                 text-[rgb(var(--color-warning))]">
                  {modActiveFilterCount > 0
                    ? `${filteredModifierApprovals.length} of ${modifierApprovals.length} pending`
                    : `${modifierApprovals.length} pending`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); loadModifierApprovals(); }}
                disabled={modifierApprovalsLoading}
                className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1
                           text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]
                           disabled:opacity-50"
              >
                Refresh
              </button>
              {sectionBOpen
                ? <ChevronUp   className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                : <ChevronDown className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
              }
            </div>
          </button>

          {sectionBOpen && (
          <div>

          {/* Partial-load warning — shown when any modifier endpoint fails */}
          {modifierLoadErrors.length > 0 && (
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/40
                            bg-[rgb(var(--color-warning))]/5 px-4 py-3 mx-4 mt-3">
              <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                {modifierLoadErrors.length} approval type(s) could not be loaded ({modifierLoadErrors.join(', ')}).
                This queue may be incomplete.
              </p>
              <button onClick={loadModifierApprovals}
                      className="text-tok-xs underline mt-1 text-[rgb(var(--color-warning))]">
                Retry
              </button>
            </div>
          )}

          {modifierApprovalsLoading && <div className="px-4 py-6"><LoadingRow /></div>}

          {/* OI-APQ-03 S733 — Filter bar: modifier type, plan name search, date range.
              Hidden while loading. Shown even when queue is empty so CA can clear filters.
              Clear All resets all four filters in one click. */}
          {!modifierApprovalsLoading && (
            <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-[rgb(var(--color-border))]">
              {/* Modifier type dropdown */}
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Type</label>
                <select
                  value={modFilterType}
                  onChange={(e) => setModFilterType(e.target.value)}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                             px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none
                             focus:border-[rgb(var(--color-primary))] min-w-[110px]"
                >
                  <option value="">All types</option>
                  {[...new Set(modifierApprovals.map((m) => m.modifier_type))].sort().map((t) => (
                    <option key={t} value={t}>
                      {_MODIFIER_LABEL[t] ?? t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan / name search */}
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan / Name</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                  <input
                    type="text"
                    value={modFilterPlan}
                    onChange={(e) => setModFilterPlan(e.target.value)}
                    placeholder="Search…"
                    className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                               pl-7 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))]
                               placeholder:italic placeholder:text-[rgb(var(--color-text-muted))]
                               focus:outline-none focus:border-[rgb(var(--color-primary))] w-40"
                  />
                </div>
              </div>

              {/* Date from */}
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">From</label>
                <input
                  type="date"
                  value={modFilterFrom}
                  onChange={(e) => setModFilterFrom(e.target.value)}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                             px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))]
                             focus:outline-none focus:border-[rgb(var(--color-primary))]"
                />
              </div>

              {/* Date to */}
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">To</label>
                <input
                  type="date"
                  value={modFilterTo}
                  onChange={(e) => setModFilterTo(e.target.value)}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                             px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))]
                             focus:outline-none focus:border-[rgb(var(--color-primary))]"
                />
              </div>

              {/* Clear all — only shown when at least one filter is active */}
              {modActiveFilterCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setModFilterType("");
                    setModFilterPlan("");
                    setModFilterFrom("");
                    setModFilterTo("");
                  }}
                  className="self-end rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1.5
                             text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]
                             flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear {modActiveFilterCount > 1 ? `(${modActiveFilterCount})` : ""}
                </button>
              )}
            </div>
          )}

          {!modifierApprovalsLoading && (
            <div className="overflow-x-auto">
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                    <tr>
                      <th className={thCls}>Type</th>
                      <th className={thCls}>Name</th>
                      <th className={thCls}>Plan</th>
                      <th className={thCls}>Submitted By</th>
                      <th className={thCls}>Amendment</th>
                      <th className="px-3 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {filteredModifierApprovals.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                          {modActiveFilterCount > 0 && modifierApprovals.length > 0
                            ? "No approvals match the active filters."
                            : "No modifier approvals pending."}
                        </td>
                      </tr>
                    )}
                    {filteredModifierApprovals.map((item, idx) => {
                      const itemId    = item.modifier_id;
                      const rejectKey = `${item.modifier_type}-${itemId}`;
                      const canApprove = currentUserId && item.submitted_by &&
                                         item.submitted_by !== currentUserId;
                      return (
                        <tr key={`${item.modifier_type}-${itemId}-${idx}`}
                            className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors">
                          {/* Type pill */}
                          <td className={tdCls}>
                            <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))]
                                             bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                              {item.modifier_label}
                            </span>
                          </td>
                          {/* Modifier name — resolved server-side */}
                          <td className={tdCls + " font-medium max-w-[160px] truncate"}>
                            {item.modifier_name ?? `ID ${itemId}`}
                          </td>
                          {/* Plan name — resolved server-side via plan_component → plan JOIN */}
                          <td className={tdCls + " max-w-[140px] truncate text-[rgb(var(--color-text-muted))]"}>
                            {item.plan_name ?? "—"}
                          </td>
                          {/* Submitted by — truncated UUID for display */}
                          <td className={tdCls + " text-[rgb(var(--color-text-muted))]"}>
                            {item.submitted_by ? String(item.submitted_by).slice(0, 8) + "…" : "—"}
                          </td>
                          {/* Amendment context */}
                          <td className={tdCls + " text-[rgb(var(--color-text-muted))]"}>
                            {item.amendment_id
                              ? <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-primary))]/30
                                                 bg-[rgb(var(--color-primary))]/5 px-2 py-0.5 text-tok-xs
                                                 text-[rgb(var(--color-primary))]">
                                  #{item.amendment_id}
                                </span>
                              : <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">Standalone</span>
                            }
                          </td>
                          {/* Actions — approve_url + reject_url come directly from backend */}
                          <td className="px-3 py-2.5">
                            {/* OI-APQ-02 S732: bundle_mode = true means the amendment approval
                                covers this modifier — no per-modifier Approve action needed.
                                Show informational badge instead of the Approve button. */}
                            {item.bundle_mode && (
                              <span className="inline-flex items-center gap-1 rounded-full border
                                               border-[rgb(var(--color-success))]/30
                                               bg-[rgb(var(--color-success))]/8
                                               px-2.5 py-1 text-tok-xs font-medium
                                               text-[rgb(var(--color-success))]">
                                <CheckCircle className="h-3 w-3" />
                                Bundle Approved
                              </span>
                            )}
                            {!item.bundle_mode && canApprove ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(item.approve_url, { method: "POST", credentials: "include" });
                                      if (!res.ok) throw new Error(`Error ${res.status}`);
                                      setModifierRejectingId(null);
                                      setModifierRejectReason("");
                                      loadModifierApprovals();
                                    } catch (e) {
                                      setPlanActionError(e.message ?? "Approval failed.");
                                    }
                                  }}
                                  className="rounded-lg border border-[rgb(var(--color-primary))]/30 px-2.5 py-1
                                             text-tok-xs font-medium text-[rgb(var(--color-primary))]
                                             hover:bg-[rgb(var(--color-primary))]/5"
                                >
                                  Approve
                                </button>
                                {modifierRejectingId === rejectKey ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={modifierRejectReason}
                                      onChange={(e) => setModifierRejectReason(e.target.value)}
                                      placeholder="Reason (required)"
                                      className="w-40 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                                    />
                                    <button
                                      disabled={!modifierRejectReason.trim()}
                                      onClick={async () => {
                                        if (!modifierRejectReason.trim()) return;
                                        try {
                                          const res = await fetch(item.reject_url, {
                                            method: "POST", credentials: "include",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ rejection_reason: modifierRejectReason.trim() }),
                                          });
                                          if (!res.ok) throw new Error(`Error ${res.status}`);
                                          setModifierRejectingId(null);
                                          setModifierRejectReason("");
                                          loadModifierApprovals();
                                        } catch (e) {
                                          setPlanActionError(e.message ?? "Return for changes failed.");
                                        }
                                      }}
                                      className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      onClick={() => { setModifierRejectingId(null); setModifierRejectReason(""); }}
                                      className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => { setModifierRejectingId(rejectKey); setModifierRejectReason(""); setPlanActionError(""); }}
                                    className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10"
                                  >
                                    Return for Changes
                                  </button>
                                )}
                              </div>
                            ) : (
                              !item.bundle_mode && (
                                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                                  {currentUserId === item.submitted_by ? "You submitted this" : "—"}
                                </span>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
          )}
        </div>

        {/* ── Section C: Approval History (S731 OI-APQ-HISTORY) ── */}
        <ModifierHistorySection
          open={sectionCOpen}
          onToggle={() => setSectionCOpen((v) => !v)}
        />

        {/* Governance note */}
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Two-actor approval required (NN-36). Self-approval blocked at API level.
          Click any component row to review details and approve.
        </p>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// PlanBulkImport — CSV bulk import for plans
// POST /api/v1/import/upload (confirmed)
// Collapsed by default — toggle button expands/collapses the panel.
// ---------------------------------------------------------------------------
function PlanBulkImport({ isLocked, period }) {
  const [open,      setOpen]      = useState(false);
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState(null);

  function reset() {
    setFile(null);
    setUploading(false);
    setResult(null);
    setError(null);
  }

  async function ensurePlanColumnMapping() {
    // S572: idempotent preflight — seeds/updates the column mapping for
    // source_system=webapp, file_type=plan before every upload.
    // Mirrors ensurePositionColumnMapping() pattern in CompAdminShell.jsx.
    // effective_date added as required (S572 fix — was missing, caused
    // plan_component.effective_date to default to CURRENT_DATE, making
    // plan components invisible to the engine for prior-period starts).
    const mappings = [
      { source_column_name: "plan_code",        target_column_name: "plan_code",        is_required: true  },
      { source_column_name: "plan_name",        target_column_name: "plan_name",        is_required: true  },
      { source_column_name: "role_code",        target_column_name: "role_code",        is_required: true  },
      { source_column_name: "segment_code",     target_column_name: "segment_code",     is_required: true  },
      { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true },
      { source_column_name: "ote_min",          target_column_name: "ote_min",          is_required: true  },
      { source_column_name: "ote_midpoint",     target_column_name: "ote_midpoint",     is_required: true  },
      { source_column_name: "ote_max",          target_column_name: "ote_max",          is_required: true  },
      { source_column_name: "ote_currency_code",target_column_name: "ote_currency_code",is_required: true  },
      { source_column_name: "base_pct",         target_column_name: "base_pct",         is_required: true  },
      { source_column_name: "variable_pct",     target_column_name: "variable_pct",     is_required: true  },
      { source_column_name: "arr_weight",       target_column_name: "arr_weight",       is_required: true  },
      { source_column_name: "mbo_weight",       target_column_name: "mbo_weight",       is_required: true  },
      { source_column_name: "nps_weight",       target_column_name: "nps_weight",       is_required: true  },
      { source_column_name: "adoption_weight",  target_column_name: "adoption_weight",  is_required: true  },
      { source_column_name: "effective_date",   target_column_name: "effective_date",   is_required: true  },
      { source_column_name: "rollup_mode",      target_column_name: "rollup_mode",      is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_system: "webapp", file_type: "plan", mappings }),
    });
    if (!res.ok) throw new Error("Failed to seed plan column mapping.");
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);

    try {
      await ensurePlanColumnMapping();

      const form = new FormData();
      form.append("file", file);
      form.append("source_system", "webapp");
      form.append("file_type", "plan");

      const res = await fetch(`${API_BASE}/import/upload`, {
        method: "POST",
        credentials: "include",
        body: form,
      });

      const data = await res.json();

      if (res.status === 422) {
        setError({ type: "all_rejected", data });
        return;
      }
      if (res.status === 207) {
        setResult({ status: 207, data });
        return;
      }
      if (!res.ok) {
        const errCode = data?.error?.code ?? data?.error ?? "UPLOAD_ERROR";
        setError({ type: "request_error", code: errCode, data });
        return;
      }
      setResult({ status: 200, data });
    } catch (e) {
      setError({ type: "network", message: e.message ?? "Upload failed." });
    } finally {
      setUploading(false);
    }
  }

  const importLog     = result?.data?.import_log ?? {};
  const rowsLoaded    = result?.data?.rows_loaded    ?? importLog.rows_loaded    ?? 0;
  const rowsUnchanged = result?.data?.rows_unchanged ?? importLog.rows_unchanged ?? 0;
  const rowsRejected  = result?.data?.rows_rejected  ?? importLog.rows_rejected  ?? 0;
  const planSessionId = importLog.plan_import_session_id ?? null;

  // Auto-collapse after successful upload
  useEffect(() => {
    if (result?.status === 200) {
      const t = setTimeout(() => setOpen(false), 3000);
      return () => clearTimeout(t);
    }
  }, [result]);

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Toggle header — always visible */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">CSV Bulk Import</span>
          {result?.status === 200 && (
            <span className="text-tok-xs text-[rgb(var(--color-success))]">✓ Upload complete</span>
          )}
          {error && (
            <span className="text-tok-xs text-[rgb(var(--color-danger))]">Upload error</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Collapsible body */}
      {open && (
        <div className="border-t border-[rgb(var(--color-border))] px-4 pb-4 pt-3">
          <p className="mb-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <span className="font-mono text-tok-xs">Plans.csv</span> to create multiple
            plans in draft status. A second comp_admin must approve each batch after import (NN-36).
          </p>

      <div className="mb-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <p className="mb-1 text-tok-xs font-semibold text-[rgb(var(--color-text))]">
          Required columns (all 16)
        </p>
        <p className="font-mono text-tok-xs leading-relaxed text-[rgb(var(--color-text-muted))]">
          plan_code, plan_name, role_code, segment_code, fiscal_period_code,
          ote_min, ote_midpoint, ote_max, ote_currency_code,
          base_pct, variable_pct,
          arr_weight, mbo_weight, nps_weight, adoption_weight,
          effective_date
        </p>
        <p className="mt-2 text-tok-xs font-semibold text-[rgb(var(--color-text))]">Optional</p>
        <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
          rollup_mode (blank → NULL for IC/AE/CSM/SE · manager_hierarchy for MGR/CRO)
        </p>
        <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          NN-25: base_pct + variable_pct = 1.0 · arr+mbo+nps+adoption weights = 1.0
        </p>
      </div>

      <div className="mb-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2.5">
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          <span className="font-medium text-[rgb(var(--color-text))]">Column mapping required.</span>{" "}
          Set up a column mapping for <span className="font-mono">source_system=webapp, file_type=plan</span> via
          the import column mapping endpoint before uploading.
        </p>
      </div>

      {result?.status === 200 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
            <div>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
                Import complete — {rowsLoaded} plan{rowsLoaded !== 1 ? "s" : ""} created in draft
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                A second comp_admin must approve this batch before plans are engine-eligible (NN-36).
              </p>
            </div>
          </div>
          {planSessionId && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <p className="mb-0.5 text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                Batch ID (plan_import_session_id)
              </p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                {planSessionId}
              </p>
              <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Share this with the approving comp_admin. They approve the batch via
                <span className="font-mono"> POST /api/v1/plans/import/approve</span>.
              </p>
            </div>
          )}
          {importLog.import_log_id && (
            <Field label="Import log ID" value={importLog.import_log_id} />
          )}
          <Button variant="secondary" onClick={reset}>Import another file</Button>
        </div>
      )}

      {result?.status === 207 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
            <div>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
                Partial import — {rowsLoaded} created, {rowsRejected} rejected
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Accepted rows are in draft. Fix rejected rows and re-upload. Duplicates are skipped automatically on re-import.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Created"   value={rowsLoaded}    />
            <Field label="Unchanged" value={rowsUnchanged} />
            <Field label="Rejected"  value={rowsRejected}  />
          </div>
          {planSessionId && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2.5">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Batch ID</p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">{planSessionId}</p>
            </div>
          )}
          {result.data?.rejected_rows?.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              <div className="sticky top-0 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2">
                <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                  {result.data.rejected_rows.length} rejected row{result.data.rejected_rows.length !== 1 ? "s" : ""}
                </p>
              </div>
              {result.data.rejected_rows.map((row, i) => (
                <div key={i} className="border-b border-[rgb(var(--color-border))] px-3 py-2">
                  <p className="font-mono text-tok-xs font-medium text-[rgb(var(--color-text))]">Row {row.row_number ?? i + 2}</p>
                  <p className="text-tok-xs text-[rgb(var(--color-danger))]">{row.error ?? row.reason ?? "Validation error"}</p>
                </div>
              ))}
            </div>
          )}
          <Button variant="secondary" onClick={reset}>Import another file</Button>
        </div>
      )}

      {error?.type === "all_rejected" && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          All rows rejected — nothing written. Correct errors and re-upload.
        </div>
      )}
      {error?.type === "request_error" && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Upload error: {error.code}
        </div>
      )}
      {error?.type === "network" && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error.message}
        </div>
      )}

      {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
      {isLocked ? (
        <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
          <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">
            Period is <strong>{period?.period_status ?? "not open"}</strong> — plan imports are not available for this period.
            Switch to an open period to import plans.
          </p>
        </div>
      ) : !result && (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90 disabled:opacity-50"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
            disabled={uploading}
          />
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplateBulkImport — CSV bulk import for templates (tenant library)
// POST /api/v1/template-library/templates (confirmed)
// ---------------------------------------------------------------------------
function TemplateBulkImport({ isLocked, period }) {
  const [file,       setFile]       = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [results,    setResults]    = useState(null);
  const [error,      setError]      = useState(null);

  function reset() {
    setFile(null);
    setSubmitting(false);
    setResults(null);
    setError(null);
  }

  async function handleImport() {
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setResults(null);

    try {
      const text = await file.text();
      const lines = text.split("\n").filter((l) => l.trim() && !l.trim().startsWith("#"));
      if (lines.length < 2) {
        setError("CSV file contains no data rows.");
        setSubmitting(false);
        return;
      }

      const headers = lines[0].split(",").map((h) => h.trim());
      const required = ["template_code", "template_name"];
      const missing = required.filter((r) => !headers.includes(r));
      if (missing.length > 0) {
        setError(`CSV missing required columns: ${missing.join(", ")}`);
        setSubmitting(false);
        return;
      }

      const dataRows = lines.slice(1).map((line) => {
        const vals = line.split(",").map((v) => v.trim());
        return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ""]));
      }).filter((r) => r.template_code);

      if (dataRows.length === 0) {
        setError("No valid data rows found.");
        setSubmitting(false);
        return;
      }

      const created = [];
      const rowErrors = [];

      for (const row of dataRows) {
        if (!row.template_code || !row.template_name) {
          rowErrors.push({ template_code: row.template_code || "(blank)", error: "template_code and template_name are required." });
          continue;
        }
        try {
          const res = await fetch(`${API_BASE}/template-library/templates`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              template_code:   row.template_code,
              template_name:   row.template_name,
              template_origin: "reverse_engineered",
              description:     row.description || null,
              configuration_snapshot: {},
            }),
          });
          const data = await res.json();
          if (!res.ok) {
            const msg = data?.detail ?? data?.error?.message ?? `HTTP ${res.status}`;
            rowErrors.push({ template_code: row.template_code, error: msg });
          } else {
            created.push({ template_code: row.template_code, template_name: row.template_name, template_id: data.template_id });
          }
        } catch (e) {
          rowErrors.push({ template_code: row.template_code, error: e.message ?? "Request failed." });
        }
      }

      setResults({ created, errors: rowErrors, total: dataRows.length });
    } catch (e) {
      setError(e.message ?? "Failed to process CSV.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card
      title="CSV Bulk Import — Tenant Library"
      action={
        <a
          download="template_import_template.csv"
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(
            "template_code,template_name,description\n" +
            "# ENT-AE-PACK,Enterprise AE Package,Core AE plan with kicker and SPIFF\n" +
            "# MM-CSM-PACK,Mid-Market CSM Package,\n"
          )}`}
          className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
        >
          <Download className="h-3.5 w-3.5" />
          Download template
        </a>
      }
    >
      <p className="mb-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Upload a CSV to create multiple template shells in the Tenant Library.
        Templates are created as empty shells — configuration and versions are added via the UI after import.
      </p>

      <div className="mb-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <p className="mb-1 text-tok-xs font-semibold text-[rgb(var(--color-text))]">Required columns</p>
        <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">template_code, template_name</p>
        <p className="mt-1.5 text-tok-xs font-semibold text-[rgb(var(--color-text))]">Optional columns</p>
        <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">description</p>
      </div>

      {results && (
        <div className="mb-4 space-y-3">
          <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
            results.errors.length === 0
              ? "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5"
              : "border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5"
          }`}>
            {results.errors.length === 0
              ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
              : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
            }
            <div>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {results.created.length} of {results.total} templates created
              </p>
              {results.errors.length > 0 && (
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {results.errors.length} failed — see details below
                </p>
              )}
            </div>
          </div>
          {results.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              {results.errors.map((e, i) => (
                <div key={i} className="border-b border-[rgb(var(--color-border))] px-3 py-2">
                  <p className="font-mono text-tok-xs font-medium text-[rgb(var(--color-text))]">{e.template_code}</p>
                  <p className="text-tok-xs text-[rgb(var(--color-danger))]">{e.error}</p>
                </div>
              ))}
            </div>
          )}
          <Button variant="secondary" onClick={reset}>Import another file</Button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}

      {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
      {isLocked ? (
        <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
          <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">
            Period is <strong>{period?.period_status ?? "not open"}</strong> — template imports are not available for this period.
            Switch to an open period to import templates.
          </p>
        </div>
      ) : !results && (
        <div className="flex flex-wrap items-center gap-3">
          {/* PlanBuilder pattern — native file input, Choose File button blue via file: pseudo-classes */}
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90 disabled:opacity-50"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
            disabled={submitting}
          />
          {/* Import — always rendered, grayed (opacity-50) until file chosen, then active */}
          <button
            onClick={handleImport}
            disabled={!file || submitting}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Importing…" : "Import"}
          </button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ComponentsTab — renders PlanBuilder directly.
// Visual Builder (ComponentBlockBuilder) deferred — toggle removed.
// ---------------------------------------------------------------------------
function ComponentsTab({ mode, period, wizardSteps, isLocked, initialPlanId, initialComponentDefaults, onConsumed }) {
  return (
    <PlanBuilder
      mode={mode}
      period={period}
      wizardSteps={wizardSteps}
      isLocked={isLocked}
      initialPlanId={initialPlanId}
      initialComponentDefaults={initialComponentDefaults}
      onConsumed={onConsumed}
    />
  );
}

// AssignmentsTab — S613 updated
// S545: Population Analysis + Conflict Detection panels above PlanAssignmentManager.
// S547: "New Bulk Assignment" button triggers BulkAssignmentWizard modal.
//       fiscalPeriod passed to wizard — period is locked from tab context.
//       PlanAssignmentManager unchanged — exception path below wizard.
// S613: PositionPlanAssignmentWorkspace mounted between Bulk Assignment bar and CoverageView.
//       Per spec §16 render order: action bar → workspace → coverage → population → conflict → PAM.
//       Workspace is the primary assignment surface. PAM accordion remains exception/single path.
// ---------------------------------------------------------------------------
function AssignmentsTab({ mode, period, isLocked }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;

  // S547 — §6 wizard mount state
  const [showBulkWizard, setShowBulkWizard] = useState(false);

  return (
    <div className="space-y-4">

      {/* ── S547: Bulk Assignment action bar ── */}
      <div className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Bulk Assignment</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Six-stage governed workflow — population analysis, conflict resolution, and all-or-nothing commit.
          </p>
        </div>
        {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION */}
        {isLocked ? (
          <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5 shrink-0">
            <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status}</span>
          </div>
        ) : (
          <button
            onClick={() => setShowBulkWizard(true)}
            disabled={!fiscalPeriodId}
            title={!fiscalPeriodId ? "Select a fiscal period to enable bulk assignment" : undefined}
            className="shrink-0 flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:opacity-40 transition"
          >
            <Workflow className="h-4 w-4" />
            New Bulk Assignment
          </button>
        )}
      </div>

      {/* ── S613 §16: Position Plan Assignment Workspace — primary assignment surface ── */}
      {/*
        Three-pane assignment workspace. Spec authority: comp_policies_spec_v2_3.md §16.
        Period inherited from shell via prop — locked within workspace.
        Virtual CSV pattern: workspace builds in-memory CSV → POST bulk-import force=true.
        No new API endpoint required.
        Mounted here, above CoverageView, per §16 AssignmentsTab render order.
      */}
      <PositionPlanAssignmentWorkspace period={period} />

      {/* ── Coverage View — §9 — motion grouping above population analysis ── */}
      <CoverageView period={period} />

      {/* ── Population Analysis — §6 ── */}
      <PopulationAnalysisPanel fiscalPeriodId={fiscalPeriodId} />

      {/* ── Conflict Detection — §6 ── */}
      <ConflictDetectionPanel fiscalPeriodId={fiscalPeriodId} />

      {/* ── S547: BulkAssignmentWizard modal ── */}
      {/*
        fiscalPeriod passed as prop — wizard inherits period from tab context.
        fiscal_period_id travels internally; wizard never displays it.
        Only period_code / period_name rendered to user.
        PlanAssignmentManager is NOT touched by this wizard mount.
      */}
      {showBulkWizard && (
        <BulkAssignmentWizard
          fiscalPeriod={period}
          onClose={() => setShowBulkWizard(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GlobalSearchResults — cross-tab search overlay
// ---------------------------------------------------------------------------
function GlobalSearchResults({ query, onClear, onNavigate }) {
  const { data: plansData,     loading: plansLoading }     = useFetch(query ? `${API_BASE}/plans?search=${encodeURIComponent(query)}&limit=5`                      : null);
  const { data: templatesData, loading: templatesLoading } = useFetch(query ? `${API_BASE}/template-library/templates?search=${encodeURIComponent(query)}&limit=5` : null);
  const { data: compsData,     loading: compsLoading }     = useFetch(query ? `${API_BASE}/plan-components?search=${encodeURIComponent(query)}&limit=5`            : null);

  const plans     = plansData?.plans     ?? plansData?.data     ?? [];
  const templates = templatesData?.templates ?? templatesData?.data ?? [];
  const comps     = compsData?.plan_components ?? compsData?.data ?? [];

  const totalLoading = plansLoading || templatesLoading || compsLoading;
  const totalResults = plans.length + templates.length + comps.length;

  if (!query) return null;

  return (
    <div className="absolute top-full left-0 right-0 z-30 mt-1 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] shadow-lg">
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-2">
        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          {totalLoading ? "Searching…" : `${totalResults} result${totalResults !== 1 ? "s" : ""}`}
        </span>
        <button onClick={onClear} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
          Clear
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-[rgb(var(--color-border))]">
        {plans.length > 0 && (
          <div className="px-4 py-2">
            <p className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Plans</p>
            {plans.map((p) => (
              <button
                key={p.plan_id}
                onClick={() => onNavigate("plans", p)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
                {p.plan_name}
              </button>
            ))}
          </div>
        )}
        {templates.length > 0 && (
          <div className="px-4 py-2">
            <p className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Templates</p>
            {templates.map((t) => (
              <button
                key={t.template_id}
                onClick={() => onNavigate("templates", t)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
                {t.template_name}
              </button>
            ))}
          </div>
        )}
        {comps.length > 0 && (
          <div className="px-4 py-2">
            <p className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Components</p>
            {comps.map((c) => (
              <button
                key={c.plan_component_id}
                onClick={() => onNavigate("components", c)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <Layers3 className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
                {c.plan_component_id}
              </button>
            ))}
          </div>
        )}
        {!totalLoading && totalResults === 0 && (
          <div className="px-4 py-6 text-center">
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No results for "{query}"</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoryTab — S548 §6 Assignment History
// Independent fetch of GET /api/v1/plan-assignments.
// Sortable columns, filterable by plan/status/period/BU, paginated.
// Row click → PositionAssignmentLedger drawer.
// No dependency on PlanAssignmentManager — fully autonomous.
// OI-ASSIGNMENT-TIMELINE-TAB: population Gantt view deferred (LOW/NOT URGENT).
// ---------------------------------------------------------------------------
const PAGE_SIZE = 25;

const SORT_FIELDS = [
  { key: "full_name",     label: "Employee"      },
  { key: "plan_code",     label: "Plan"          },
  { key: "fiscal_period", label: "Period"        },
  { key: "effective_date",label: "Effective Date"},
  { key: "end_date",      label: "End Date"      },
  { key: "assignment_status", label: "Status"    },
];

function HistoryTab() {
  const [allAssignments, setAllAssignments] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);

  // Filters
  const [filterPlan,    setFilterPlan]    = useState("");
  const [filterStatus,  setFilterStatus]  = useState("");
  const [filterPeriod,  setFilterPeriod]  = useState("");
  const [filterBU,      setFilterBU]      = useState("");
  const [filterSegment, setFilterSegment] = useState("");
  const [filterSearch,  setFilterSearch]  = useState("");

  // Sort
  const [sortKey,  setSortKey]  = useState("effective_date");
  const [sortDir,  setSortDir]  = useState("desc"); // asc | desc

  // Pagination
  const [page, setPage] = useState(1);

  // Ledger drawer
  const [ledgerPosition, setLedgerPosition] = useState(null);

  // Fetch all assignments — History tab owns its own data
  function load() {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/plan-assignments`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        const raw = data?.plan_assignments ?? data?.assignments ?? data?.data ?? [];
        setAllAssignments(raw);
      })
      .catch((e) => setError(e.message ?? "Unable to load assignment history."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  // Derive filter options from data
  const planOptions    = useMemo(() => [...new Set(allAssignments.map((a) => a.plan_code   ?? a.plan_name ?? "").filter(Boolean))].sort(), [allAssignments]);
  const periodOptions  = useMemo(() => [...new Set(allAssignments.map((a) => a.fiscal_period_code ?? a.fiscal_period ?? "").filter(Boolean))].sort(), [allAssignments]);
  const buOptions      = useMemo(() => [...new Set(allAssignments.map((a) => a.business_unit_code ?? a.business_unit ?? "").filter(Boolean))].sort(), [allAssignments]);
  const segmentOptions = useMemo(() => [...new Set(allAssignments.map((a) => a.segment_name ?? a.segment_code ?? "").filter(Boolean))].sort(), [allAssignments]);

  // Filter
  const filtered = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    return allAssignments.filter((a) => {
      if (filterPlan    && (a.plan_code ?? a.plan_name ?? "") !== filterPlan)   return false;
      if (filterStatus  && a.assignment_status !== filterStatus)                 return false;
      if (filterPeriod  && (a.fiscal_period_code ?? a.fiscal_period ?? "") !== filterPeriod) return false;
      if (filterBU      && (a.business_unit_code ?? a.business_unit ?? "") !== filterBU) return false;
      if (filterSegment && (a.segment_name ?? a.segment_code ?? "") !== filterSegment) return false;
      if (q && !(
        (a.full_name ?? "").toLowerCase().includes(q) ||
        (a.plan_code ?? a.plan_name ?? "").toLowerCase().includes(q) ||
        (a.employee_id ?? "").toLowerCase().includes(q) ||
        (a.fiscal_period_code ?? a.fiscal_period ?? "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [allAssignments, filterPlan, filterStatus, filterPeriod, filterBU, filterSegment, filterSearch]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";
      if (sortKey === "effective_date" || sortKey === "end_date") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = String(av).toLowerCase();
        bv = String(bv).toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [filterPlan, filterStatus, filterPeriod, filterBU, filterSegment, filterSearch]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  function SortIcon({ field }) {
    if (sortKey !== field) return <ChevronUp className="h-3 w-3 opacity-30" />;
    return sortDir === "asc"
      ? <ChevronUp   className="h-3 w-3 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="h-3 w-3 text-[rgb(var(--color-primary))]" />;
  }

  function clearFilters() {
    setFilterPlan(""); setFilterStatus(""); setFilterPeriod("");
    setFilterBU(""); setFilterSegment(""); setFilterSearch("");
  }

  const hasFilters = filterPlan || filterStatus || filterPeriod || filterBU || filterSegment || filterSearch;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Assignment History</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            All plan assignments across fiscal periods. Click any row to view full position ledger.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="min-w-48 flex-1">
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Employee, plan, period…"
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:outline-none"
              />
            </div>
          </div>

          {/* Plan filter */}
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Plan</label>
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">All plans</option>
              {planOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="superseded">Superseded</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Period filter */}
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Period</label>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">All periods</option>
              {periodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* BU filter */}
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Business Unit</label>
            <select
              value={filterBU}
              onChange={(e) => setFilterBU(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">All BUs</option>
              {buOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* Segment filter */}
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Segment</label>
            <select
              value={filterSegment}
              onChange={(e) => setFilterSegment(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">All segments</option>
              {segmentOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Clear */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>

        {/* Result count */}
        <p className="mt-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {loading ? "Loading…" : `${filtered.length} of ${allAssignments.length} assignments`}
          {hasFilters && " (filtered)"}
        </p>
      </div>

      {/* Error */}
      {error && <ErrorNote message={error} />}

      {/* Table */}
      {!error && (
        <div className="overflow-hidden rounded-xl border border-[rgb(var(--color-border))]">
          <div className="max-h-[560px] overflow-y-auto overflow-x-auto">
            <table className="w-full text-tok-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                  {[
                    { key: "full_name",          label: "Employee"   },
                    { key: "plan_code",           label: "Plan"       },
                    { key: "fiscal_period",       label: "Period"     },
                    { key: "business_unit",       label: "BU"         },
                    { key: "effective_date",      label: "Effective"  },
                    { key: "end_date",            label: "End"        },
                    { key: "assignment_status",   label: "Status"     },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="cursor-pointer select-none px-4 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                    >
                      <span className="flex items-center gap-1">
                        {label}
                        <SortIcon field={key} />
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    Ledger
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8">
                      <LoadingRow />
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                      No assignments match your filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((a, idx) => {
                    const statusCfg = { active: "text-[rgb(var(--color-success))]", superseded: "text-[rgb(var(--color-text-muted))]", cancelled: "text-[rgb(var(--color-danger))]" };
                    return (
                      <tr
                        key={a.plan_assignment_id ?? idx}
                        className="bg-[rgb(var(--color-surface))] transition hover:bg-[rgb(var(--color-surface-muted))]"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
                            <div>
                              <p className="font-medium text-[rgb(var(--color-text))]">
                                {a.full_name ?? a.employee_id ?? "—"}
                              </p>
                              {a.employee_id && a.full_name && (
                                <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{a.employee_id}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-[rgb(var(--color-text))]">
                            {a.plan_code ?? a.plan_name ?? "—"}
                          </p>
                          {a.plan_version_resolved && (
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">v{a.plan_version_resolved}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                          {a.fiscal_period_label ?? a.fiscal_period_code ?? a.fiscal_period ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                          {a.business_unit_code ?? a.business_unit ?? "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-tok-xs text-[rgb(var(--color-text))]">
                          {a.effective_date ?? "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-tok-xs text-[rgb(var(--color-text))]">
                          {a.end_date ?? <span className="text-[rgb(var(--color-text-muted))]">Open</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-tok-xs font-medium capitalize ${statusCfg[a.assignment_status] ?? ""}`}>
                            {a.assignment_status ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setLedgerPosition({
                              position_id:  a.position_id,
                              full_name:    a.full_name ?? a.employee_id,
                              employee_id:  a.employee_id,
                              role_name:    a.role_name ?? a.role_code,
                              business_unit_code: a.business_unit_code ?? a.business_unit,
                              hire_date:    a.hire_date,
                            })}
                            className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:border-[rgb(var(--color-primary))]/40 hover:text-[rgb(var(--color-primary))]"
                          >
                            <History className="h-3 w-3" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Page {page} of {totalPages} · {sorted.length} total
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface))] disabled:opacity-40"
                >
                  Previous
                </button>
                {/* Page number buttons — show up to 5 around current */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const n = start + i;
                  return n <= totalPages ? (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`rounded-lg px-3 py-1 text-tok-xs font-medium transition ${
                        n === page
                          ? "bg-[rgb(var(--color-primary))] text-white"
                          : "border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface))]"
                      }`}
                    >
                      {n}
                    </button>
                  ) : null;
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface))] disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PositionAssignmentLedger drawer */}
      <AnimatePresence>
        {ledgerPosition && (
          <PositionAssignmentLedger
            position={ledgerPosition}
            onClose={() => setLedgerPosition(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
export default function CompensationPoliciesWorkspace({ mode, period, wizardSteps, wizardSessionId }) {
  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  const isLocked = period?.period_status !== "open";

  const [searchParams, setSearchParams] = useSearchParams();

  const paramTab    = searchParams.get("tab");
  // OI-CRL-DESIGN-PLAN-ROLE-FILTER (S622):
  // "Design Plan" button navigates to /admin/compensation-policies?role_id=...&tab=plans
  // Read role_id here and forward it to PlansLandingPage as initialRoleId so the
  // role filter is pre-populated on arrival. Consumed once — PlansLandingPage owns its
  // own filter state after mount.
  const paramRoleId = searchParams.get("role_id");
  const storedTab  = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SESSION_KEY) : null;
  const initialTab = VALID_TABS.includes(paramTab) ? paramTab
    : VALID_TABS.includes(storedTab) ? storedTab
    : "plans";

  const [activeTab,   setActiveTab]   = useState(initialTab);
  const [selectedPlanId,     setSelectedPlanId]     = useState(null);
  const [selectedPlanStatus, setSelectedPlanStatus] = useState(null);

  // Sync activeTab when the URL ?tab= param changes — required because the
  // CompAdminShell sidebar uses NavLink which updates the URL externally.
  // Without this, the component ignores URL changes after mount.
  useEffect(() => {
    if (paramTab && VALID_TABS.includes(paramTab) && paramTab !== activeTab) {
      setActiveTab(paramTab);
      setSelectedPlanId(null);
      setSelectedPlanStatus(null);
      if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SESSION_KEY, paramTab);
    }
  }, [paramTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [search,      setSearch]      = useState("");
  const [searchOpen,  setSearchOpen]  = useState(false);

  // S680: post-deploy bridge — set by PlanLibrary after path=plan_with_components deploy.
  // { planId, templateName, componentDefaults } — consumed once by ComponentsTab → PlanBuilder.
  const [pendingComponentConfig, setPendingComponentConfig] = useState(null);

  // S698: called by PlanLibrary "Configure Components →" button.
  // Switches to Plans tab and opens the deployed plan in wizard mode (draft → mode='wizard').
  // Cannot use switchTab() — it calls setSelectedPlanId(null) which would clear the plan.
  // S680 behaviour (route to ComponentsTab/PlanBuilder) superseded by wizard (AD-79).
  function handleConfigureComponents(deployedPlan) {
    if (!deployedPlan?.planId) return;
    setActiveTab("plans");
    setSelectedPlanId(String(deployedPlan.planId));
    setSelectedPlanStatus('draft');
    setSearch("");
    setSearchOpen(false);
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SESSION_KEY, "plans");
    setSearchParams({}, { replace: true });
  }

  // S682: Plans tab → plan detail bridge.
  // Called by PlansLandingPage onPlanClick — renders PlanDetailView inline in the Plans tab.
  // CA never leaves the workspace. Back button clears selectedPlanId to return to list.
  //
  // Wizard vs admin mode (AD-72):
  //   draft plans       → mode='wizard'  (CA composing — step flow with Back/Next)
  //   all other statuses → mode='admin'  (approved, pending_approval, withdrawn — amendment flow)
  // NOTE: selectedPlanId / selectedPlanStatus declared above alongside the URL-sync useEffect.

  function handlePlanSelected(planId, planStatus) {
    setSelectedPlanId(String(planId));
    setSelectedPlanStatus(planStatus ?? null);
  }

  function handleBackToPlans() {
    setSelectedPlanId(null);
    setSelectedPlanStatus(null);
  }

  // Approvals badge count
  // FIX OI-APPROVALS-TAB-COMPONENT-FILTER: exclude components whose parent
  // plan is already approved — they are not actionable.
  const { data: pendingCompsData } = useFetch(`${API_BASE}/plan-components`);
  const { data: pendingPlansData } = useFetch(`${API_BASE}/plans?plan_status=pending_approval`);
  const { data: allPlansForBadge } = useFetch(`${API_BASE}/plans`);
  const _badgePlanMap = Object.fromEntries(
    (allPlansForBadge?.plans ?? allPlansForBadge?.data ?? []).map((p) => [String(p.plan_id), p])
  );
  const pendingComps  = (pendingCompsData?.plan_components ?? pendingCompsData?.data ?? []).filter(
    (c) => !c.approved_by && !c.approved_at && _badgePlanMap[String(c.plan_id)]?.plan_status !== "approved"
  ).length;
  const pendingPlans  = (pendingPlansData?.data ?? pendingPlansData?.plans ?? []).length;
  const approvalCount = pendingComps + pendingPlans;

  // §9 Ongoing reminder — approved plans not yet in template library
  const { data: allApprovedPlansData } = useFetch(`${API_BASE}/plans?plan_status=approved`);
  const { data: tenantTemplatesData }  = useFetch(`${API_BASE}/template-library/templates`);
  const approvedPlanCount   = (allApprovedPlansData?.data ?? allApprovedPlansData?.plans ?? []).length;
  const tenantTemplateCount = (tenantTemplatesData?.templates ?? tenantTemplatesData?.data ?? [])
    .filter((t) => !t.is_publisher_certified).length;
  // Only show reminder when there are approved plans not yet templated — heuristic:
  // if approved plans > tenant templates, some plans likely haven't been converted yet
  const plansWithoutTemplates = Math.max(0, approvedPlanCount - tenantTemplateCount);
  const showTemplateReminder  = plansWithoutTemplates > 0;

  function switchTab(key) {
    setActiveTab(key);
    setSearch("");
    setSearchOpen(false);
    setSelectedPlanId(null); // clear plan detail on any tab switch, including returning to plans
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SESSION_KEY, key);
    setSearchParams(key === "plans" ? {} : { tab: key }, { replace: true });
  }

  function handleSearchNav(tab, _item) {
    switchTab(tab);
    setSearch("");
    setSearchOpen(false);
  }

  // CHANGE-15 S737 — CA-2 full-page review route (AD-207).
  // When reviewComponentId param is present, render PlanComponentReviewPage full-screen
  // instead of the workspace. Early return before line 3294 per spec (L-135 confirmed safe).
  // Rule 23: PlanComponentReviewPage calls useAuth() directly — no prop-passing from here.
  const reviewComponentId = searchParams.get("reviewComponentId");
  if (reviewComponentId) {
    return (
      <PlanComponentReviewPage
        planComponentId={reviewComponentId}
        onClose={() => {
          // Clear the param and return to approvals tab
          setSearchParams({ tab: "approvals" }, { replace: true });
        }}
      />
    );
  }

  return (
    <div className="space-y-4">

      {/* Global search — tab nav handled by CompAdminShell sidebar */}
      <div className="flex justify-end">
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
          <input
            type="text"
            value={search}
            onFocus={() => setSearchOpen(true)}
            onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
            placeholder="Search all…"
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-2 pl-9 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setSearchOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {searchOpen && search.length >= 2 && (
            <GlobalSearchResults
              query={search}
              onClear={() => { setSearch(""); setSearchOpen(false); }}
              onNavigate={handleSearchNav}
            />
          )}
        </div>
      </div>

      {/* Tab content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
      >
        {activeTab === "plans" && (
          <div className="space-y-4">
            {selectedPlanId ? (
              /* S682: plan detail view embedded inline — CA stays in workspace */
              <div>
                <button
                  type="button"
                  onClick={handleBackToPlans}
                  className="mb-4 flex items-center gap-1.5 text-tok-sm text-[rgb(var(--color-primary))] hover:underline focus:outline-none"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  All Plans
                </button>
                <PlanDetailView
                  mode={selectedPlanStatus === 'draft' ? 'wizard' : 'admin'}
                  period={period}
                  planId={selectedPlanId}
                />
              </div>
            ) : (
              /* Default: plans list */
              <>
                {/* §9 Ongoing reminder — subtle indicator, not intrusive */}
                {showTemplateReminder && (
                  <div className="flex items-center justify-between rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2.5">
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      <span className="font-medium text-[rgb(var(--color-text))]">{plansWithoutTemplates}</span>{" "}
                      approved plan{plansWithoutTemplates !== 1 ? 's have' : ' has'} not yet been added to your template library.
                    </p>
                    <button
                      type="button"
                      onClick={() => switchTab("templates")}
                      className="ml-4 shrink-0 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline"
                    >
                      Review →
                    </button>
                  </div>
                )}
                <PlanBulkImport isLocked={isLocked} period={period} />
                <PlansLandingPage
                  period={period}
                  isLocked={isLocked}
                  initialRoleId={paramRoleId}
                  onPlanClick={handlePlanSelected}
                />
              </>
            )}
          </div>
        )}

        {activeTab === "templates" && (
          <PlanLibrary
            period={period}
            isLocked={isLocked}
            onConfigureComponents={handleConfigureComponents}
          />
        )}

        {activeTab === "components" && (
          <ComponentsTab
            mode={mode}
            period={period}
            wizardSteps={wizardSteps}
            isLocked={isLocked}
            initialPlanId={pendingComponentConfig?.planId ?? null}
            initialComponentDefaults={pendingComponentConfig?.componentDefaults ?? null}
            onConsumed={() => setPendingComponentConfig(null)}
          />
        )}

        {activeTab === "assignments" && (
          <AssignmentsTab mode={mode} period={period} isLocked={isLocked} />
        )}

        {activeTab === "approvals" && (
          <PlanApprovalPanel mode={mode} period={period} wizardSessionId={wizardSessionId} />
        )}

        {activeTab === "history" && (
          <HistoryTab />
        )}
      </motion.div>

    </div>
  );
}
